import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationCircleIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import { Button } from '@dashboard/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import Tooltip, { InfoTooltip } from '@dashboard/components/Tooltip';
import { formatDateTime } from '@dashboard/lib/format-date';
import { cn } from '@dashboard/lib/utils';

const FILTER_OPTIONS = {
  blocked: { value: 'blocked', label: 'Blocked' },
  unblocked: { value: 'unblocked', label: 'Unblocked' },
};

const BlockedCustomerTable = ({
  data,
  isLoading,
  isError,
  filter = 'blocked',
  onFilterChange,
  onUnblock,
  onBlock,
  onEditReason,
}) => {
  const columns = useMemo(() => {
    // Only the unblocked list has an unblock to report; on the blocked list
    // every row would read "Unknown".
    const unblockedColumns =
      filter === 'unblocked'
        ? [
            {
              accessorKey: 'unblockedByName',
              header: 'Unblocked by',
              cell: ({ row }) =>
                row.original.unblockedByName ? (
                  <div className="text-sm">{row.original.unblockedByName}</div>
                ) : (
                  <div className="text-sm text-muted-foreground">Unknown</div>
                ),
            },
            {
              accessorKey: 'unblockedAt',
              header: 'Unblocked on',
              cell: ({ row }) =>
                row.original.unblockedAt ? (
                  <div className="text-sm">{formatDateTime(row.original.unblockedAt)}</div>
                ) : (
                  <div className="text-sm text-muted-foreground">Unknown</div>
                ),
            },
          ]
        : [];

    return [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <div className="font-medium">{row.original.email}</div>,
      },
      {
        accessorKey: 'note',
        header: 'Reason',
        cell: ({ row }) => {
          const { note } = row.original;

          return (
            <Tooltip label={note} triggerClassName="block max-w-[280px] text-left">
              <div
                className={cn('text-sm truncate max-w-[280px]', !note && 'text-muted-foreground')}
              >
                {note || 'No reason given'}
              </div>
            </Tooltip>
          );
        },
        meta: { className: 'max-w-[280px]' },
      },
      {
        accessorKey: 'blockedByName',
        header: 'Blocked by',
        cell: ({ row }) =>
          row.original.blockedByName ? (
            <div className="text-sm">{row.original.blockedByName}</div>
          ) : (
            <div className="text-sm text-muted-foreground">Unknown</div>
          ),
      },
      {
        accessorKey: 'blockedAt',
        header: 'Blocked on',
        cell: ({ row }) => (
          <div className="text-sm">
            {formatDateTime(row.original.blockedAt || row.original.createdAt)}
          </div>
        ),
      },
      ...unblockedColumns,
      {
        accessorKey: 'checkoutAttempts',
        header: (
          <>
            Attempts
            <InfoTooltip>
              Total attempts to complete a checkout, event or form page after being blocked.
            </InfoTooltip>
          </>
        ),
        cell: ({ row }) => <div className="text-sm">{row.original.checkoutAttempts ?? 0}</div>,
      },
      {
        accessorKey: 'lastBlockedAt',
        header: 'Last attempt',
        cell: ({ row }) =>
          row.original.lastBlockedAt ? (
            <div className="text-sm">{formatDateTime(row.original.lastBlockedAt)}</div>
          ) : (
            <div className="text-sm text-muted-foreground">Never</div>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" aria-label={`Actions for ${row.original.email}`}>
                <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Disabled rather than dropped, so the menu keeps the same shape
                  on every row. Not asChild when disabled: the anchor would stay
                  navigable underneath. */}
              {row.original.customer ? (
                <DropdownMenuItem asChild>
                  <Link to={`/customers/${row.original.customer}`}>View customer</Link>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem disabled>View customer</DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onEditReason(row.original)}>
                Edit reason
              </DropdownMenuItem>
              {row.original.isDeleted ? (
                <DropdownMenuItem onSelect={() => onBlock(row.original)}>
                  Block email
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem variant="destructive" onSelect={() => onUnblock(row.original)}>
                  Unblock email
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        meta: { className: 'w-px text-right' },
      },
    ];
  }, [filter, onUnblock, onBlock, onEditReason]);

  if (isError) {
    return (
      <Alert variant="destructive">
        <ExclamationCircleIcon />
        <AlertTitle>Could not load your blocked emails</AlertTitle>
        <AlertDescription>Refresh the page to try again.</AlertDescription>
      </Alert>
    );
  }

  const blockedCustomers = data?.blockedCustomers ?? [];

  const emptyState = (
    <EmptyState>
      <EmptyState.Subtitle>
        {filter === 'unblocked' ? 'No unblocked emails yet.' : 'No blocked emails yet.'}
      </EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <DataGrid
      columns={columns}
      data={blockedCustomers}
      isLoading={isLoading}
      emptyState={emptyState}
      toolbar={{
        customActions: (
          <FilterInput options={FILTER_OPTIONS} value={filter} onChange={onFilterChange} />
        ),
      }}
    />
  );
};

export default BlockedCustomerTable;
